import type { DomainSettings } from "@/domain-setting-types";
import {
  type AuthorityGroupPolicy,
  CreateTopicAuthorityGroupPolicyDefault,
  ShowReactionDatePolicyDefault,
  type ShowReactionDateType,
  ShowReactionDateTypeDefault,
  UpdateTopicAuthorityGroupPolicyDefault,
} from "@/support-acl-types";
import type {
  AttachmentFileTypes,
  AttachmentPolicy,
  DownloadDeviceTypes,
  DownloadPolicy,
} from "@/suppport-attachment-types";
import {
  AttachmentFileTypesDefault,
  AttachmentPolicyDefault,
  DownloadDeviceTypesDefault,
  DownloadDeviceTypesNone,
  DownloadPolicyDefault,
} from "@/suppport-attachment-types";

export class Domain {
  // 組織ID
  public readonly domainId: string;

  // 話題 > 作成権限 (デフォルト { admin: true, user: true })
  public readonly createTopicAuthorityGroupPolicy: AuthorityGroupPolicy;

  // 話題 > 更新権限 (デフォルト { admin: true })
  public readonly updateTopicAuthorityGroupPolicy: AuthorityGroupPolicy;

  // 添付設定
  public readonly attachmentPolicy: AttachmentPolicy;
  public readonly attachmentFileType: AttachmentFileTypes;

  // ダウンロード設定
  public readonly downloadPolicy: DownloadPolicy;
  public readonly downloadDeviceType: DownloadDeviceTypes;

  // いいね！の日時表示設定
  public readonly showReactionDatePolicy: boolean;
  public readonly showReactionDateType: ShowReactionDateType;

  public constructor(obj: { domainId: string } & Partial<Domain>) {
    this.domainId = obj.domainId; // 組織IDは確実に存在するようにする
    this.createTopicAuthorityGroupPolicy = obj.createTopicAuthorityGroupPolicy
      ? {
          admin: obj.createTopicAuthorityGroupPolicy.admin,
          user: obj.createTopicAuthorityGroupPolicy.user,
        }
      : CreateTopicAuthorityGroupPolicyDefault;
    this.updateTopicAuthorityGroupPolicy = obj.updateTopicAuthorityGroupPolicy
      ? { admin: obj.updateTopicAuthorityGroupPolicy.admin }
      : UpdateTopicAuthorityGroupPolicyDefault;
    this.attachmentPolicy = obj.attachmentPolicy || AttachmentPolicyDefault;
    if (obj.attachmentFileType) {
      this.attachmentFileType = {
        image: obj.attachmentFileType.image,
        movie: obj.attachmentFileType.movie,
        pdf: obj.attachmentFileType.pdf,
        office: obj.attachmentFileType.office,
      };
    } else {
      this.attachmentFileType = AttachmentFileTypesDefault;
    }
    this.downloadPolicy = obj.downloadPolicy || DownloadPolicyDefault;
    if (obj.downloadDeviceType) {
      this.downloadDeviceType = {
        web: obj.downloadDeviceType.web,
        ios: obj.downloadDeviceType.ios,
        android: obj.downloadDeviceType.android,
        desktop: obj.downloadDeviceType.desktop,
      };
    } else {
      this.downloadDeviceType =
        this.downloadPolicy === "ALLOW"
          ? DownloadDeviceTypesDefault
          : DownloadDeviceTypesNone;
    }
    this.showReactionDatePolicy =
      obj.showReactionDatePolicy ?? ShowReactionDatePolicyDefault;
    this.showReactionDateType =
      obj.showReactionDateType ?? ShowReactionDateTypeDefault;
  }

  // 組織IDを指定して作成する
  public static create(obj: { domainId: string } & Partial<Domain>) {
    return new Domain(obj);
  }

  public static createFrom(obj: { id: string } & Partial<DomainSettings>) {
    return new Domain({
      domainId: obj.id,
      createTopicAuthorityGroupPolicy:
        obj.createTopicAuthorityGroupPolicy as AuthorityGroupPolicy,
      updateTopicAuthorityGroupPolicy:
        obj.updateTopicAuthorityGroupPolicy as AuthorityGroupPolicy,
      attachmentPolicy: obj.attachmentPolicy,
      attachmentFileType: obj.attachmentFileType as AttachmentFileTypes,
      downloadPolicy: obj.downloadPolicy,
      downloadDeviceType: obj.downloadDeviceType as DownloadDeviceTypes,
      showReactionDatePolicy: obj.showReactionDatePolicy,
      showReactionDateType: obj.showReactionDateType,
    });
  }
}
